import { useState, useEffect } from 'react';
import { RecaptchaVerifier, signInWithPhoneNumber, ConfirmationResult } from 'firebase/auth';
import { collection, doc, setDoc, getDocs, deleteDoc, query, where } from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import { useTenant } from '@/contexts/TenantContext';
import { Card, CardHeader } from '@/components/ui/Card';
import { Smartphone, Check, Loader2, Trash2 } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

// Prevent TS error for window.recaptchaVerifier
declare global {
  interface Window {
    recaptchaVerifier: any;
  }
}

export function PhoneVerification() {
  const { t } = useLanguage();
  
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [verifiedNumbers, setVerifiedNumbers] = useState<string[]>([]);
  const [isLoadingNumbers, setIsLoadingNumbers] = useState(true);

  // Get the current active meter ID from the TenantContext
  const { sheet } = useTenant();
  const clientId = sheet.id || import.meta.env.VITE_CLIENT_ID || 'master';

  // Initialize reCAPTCHA
  useEffect(() => {
    // Disable reCAPTCHA in local development to prevent invalid-app-credential errors
    if (import.meta.env.DEV) {
      auth.settings.appVerificationDisabledForTesting = true;
    }

    if (!window.recaptchaVerifier && auth) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
      });
    }

    return () => {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    };
  }, []);

  // Fetch verified numbers
  useEffect(() => {
    const fetchNumbers = async () => {
      if (!clientId) return;
      try {
        const q = query(
          collection(db, 'clients', clientId, 'alert_recipients'),
          where('verified', '==', true)
        );
        const snapshot = await getDocs(q);
        const numbers = snapshot.docs.map(doc => doc.id);
        setVerifiedNumbers(numbers);
      } catch (err) {
        console.error("Failed to fetch verified numbers:", err);
      } finally {
        setIsLoadingNumbers(false);
      }
    };
    fetchNumbers();
  }, [clientId, success]);

  const handleSendOTP = async () => {
    if (!phoneNumber) {
      setError('Please enter a phone number with country code (e.g. +1234567890)');
      return;
    }
    
    setIsSending(true);
    setError('');
    
    try {
      if (!window.recaptchaVerifier) {
        throw new Error('reCAPTCHA not initialized');
      }
      
      const formattedNumber = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
      const confirmation = await signInWithPhoneNumber(auth, formattedNumber, window.recaptchaVerifier);
      setConfirmationResult(confirmation);
      setSuccess(`OTP sent to ${formattedNumber}`);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to send OTP. Ensure phone number has country code.');
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!verificationCode || !confirmationResult) return;
    
    setIsVerifying(true);
    setError('');
    
    try {
      const result = await confirmationResult.confirm(verificationCode);
      const user = result.user;
      
      if (user.phoneNumber && clientId) {
        // Save to Firestore
        await setDoc(doc(db, 'clients', clientId, 'alert_recipients', user.phoneNumber), {
          phoneNumber: user.phoneNumber,
          verified: true,
          addedAt: new Date().toISOString()
        });
        
        setSuccess('Phone number verified and alerts enabled!');
        setPhoneNumber('');
        setVerificationCode('');
        setConfirmationResult(null);
      }
    } catch (err: any) {
      console.error(err);
      setError('Invalid OTP code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRemoveNumber = async (num: string) => {
    if (!clientId || !window.confirm(`Remove ${num} from receiving alerts?`)) return;
    
    try {
      await deleteDoc(doc(db, 'clients', clientId, 'alert_recipients', num));
      setVerifiedNumbers(prev => prev.filter(n => n !== num));
    } catch (err) {
      console.error("Failed to remove number:", err);
    }
  };

  return (
    <Card>
      <CardHeader
        eyebrow={t("Alert Recipients")}
        title={t("WhatsApp Alert Numbers")}
      />
      <div className="text-xs text-ink-500 mb-4 px-4">
        {t("Add phone numbers below to receive WhatsApp alerts for critical anomalies and system offline events.")}
      </div>

      <div id="recaptcha-container"></div>

      <div className="px-4 pb-4">
        {error && (
          <div className="mb-4 text-xs font-semibold text-red-600 bg-red-50 p-2 rounded-lg border border-red-200">
            {error}
          </div>
        )}
        
        {success && !confirmationResult && (
          <div className="mb-4 text-xs font-semibold text-emerald-700 bg-emerald-50 p-2 rounded-lg border border-emerald-200">
            {success}
          </div>
        )}

        {/* Verified Numbers List */}
        <div className="mb-6 space-y-2">
          {isLoadingNumbers ? (
            <div className="text-xs text-ink-400 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading recipients...
            </div>
          ) : verifiedNumbers.length === 0 ? (
            <div className="text-xs text-ink-400 italic">No numbers added yet.</div>
          ) : (
            verifiedNumbers.map(num => (
              <div key={num} className="flex items-center justify-between bg-ink-50 p-2 rounded-lg border border-ink-100">
                <div className="flex items-center gap-2 text-sm font-semibold text-ink-700">
                  <Smartphone className="w-4 h-4 text-emerald-500" />
                  {num}
                </div>
                <button 
                  onClick={() => handleRemoveNumber(num)}
                  className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors"
                  title="Remove number"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Add New Number Form */}
        {!confirmationResult ? (
          <div className="flex gap-2">
            <input
              type="tel"
              placeholder="+1234567890"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="flex-1 rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <button
              onClick={handleSendOTP}
              disabled={isSending || !phoneNumber}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
            >
              {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send OTP"}
            </button>
          </div>
        ) : (
          <div className="bg-brand-50 border border-brand-100 p-3 rounded-xl space-y-3">
            <div className="text-xs font-medium text-brand-700">
              Enter the 6-digit code sent to {phoneNumber}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="123456"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className="flex-1 rounded-lg border border-brand-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              <button
                onClick={handleVerifyOTP}
                disabled={isVerifying || !verificationCode}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
              >
                {isVerifying ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Verify
                  </>
                )}
              </button>
            </div>
            <button 
              onClick={() => {
                setConfirmationResult(null);
                setError('');
                setSuccess('');
              }}
              className="text-xs text-ink-500 hover:text-ink-700 font-medium"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}
